import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type VideoSubscriptionPlan = {
  id: string;
  slug: string;
  name: string;
  name_es: string | null;
  tagline: string | null;
  tagline_es: string | null;
  description: string | null;
  description_es: string | null;
  features: string[];
  features_es: string[];
  price_amount: number;
  price_amount_mxn: number | null;
  price_amount_cop: number | null;
  compare_at_amount: number | null;
  compare_at_amount_mxn: number | null;
  compare_at_amount_cop: number | null;
  currency: string;
  interval: "month" | "year" | "one_time";
  interval_count: number;
  trial_days: number;
  stripe_product_id: string | null;
  stripe_price_id: string | null;
  stripe_price_id_mxn: string | null;
  stripe_price_id_cop: string | null;
  is_active: boolean;
  is_popular: boolean;
  display_order: number;
  cta_label: string | null;
  cta_label_es: string | null;
  created_at: string;
  updated_at: string;
};

const normalize = (row: any): VideoSubscriptionPlan => ({
  ...row,
  features: Array.isArray(row.features) ? row.features : [],
  features_es: Array.isArray(row.features_es) ? row.features_es : [],
});

/**
 * Fetches active video subscription plans from the DB.
 * Used by public pricing UIs (Pricing, FinalCTA).
 */
export function useVideoSubscriptionPlans(opts?: { includeInactive?: boolean }) {
  const includeInactive = opts?.includeInactive ?? false;
  const [plans, setPlans] = useState<VideoSubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = async () => {
    setLoading(true);
    let query = supabase
      .from("video_subscription_plans")
      .select("*")
      .order("display_order", { ascending: true });
    if (!includeInactive) query = query.eq("is_active", true);
    const { data, error } = await query;
    if (error) {
      setError(error.message);
      setPlans([]);
    } else {
      setError(null);
      setPlans((data || []).map(normalize));
    }
    setLoading(false);
  };

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive]);

  return { plans, loading, error, refetch };
}
